import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Close as CloseIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from '../contexts/LanguageContext';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  
  const { t, language } = useLanguage();

  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'email' | 'verification' | 'newPassword'>('email');
  const [otpCode, setOtpCode] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    setError(null);
  };

  const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtpCode(event.target.value);
    setError(null);
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /\S+@\S+\.\S+/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push(t('rule.password.length'));
    }

    if (!/(?=.*[a-z])/.test(password)) {
      errors.push(t('rule.password.lower'));
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push(t('rule.password.upper'));
    }

    if (!/(?=.*\d)/.test(password)) {
      errors.push(t('rule.password.digit'));
    }

    if (!/(?=.*[!@#$%^&*])/.test(password)) {
      errors.push(t('rule.password.special'));
    }

    return errors;
  };

  const handleRequestOtp = async () => {
    if (!formData.email.trim()) {
      setError(t('error.email.empty'));
      return;
    }

    if (!validateEmail(formData.email)) {
      setError(t('error.email.invalid'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/request-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otpType: 'CHANGE_PASSWORD',
          language
        }),
      });

      if (response.ok) {
        setStep('verification');
        blobticMessage.success(t('success.codeSent'), t('success.codeSentDesc'));
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.code ? t(errorData.code) : (errorData.message || t('error.connection'));
        setError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      setError(t('error.otp.empty'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otp: otpCode,
          otpType: 'CHANGE_PASSWORD',
          language
        }),
      });

      if (response.ok) {
        setStep('newPassword');
        blobticMessage.success(t('success.codeVerified'), t('success.codeVerifiedDesc'));
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.code ? t(errorData.code) : (errorData.message || t('error.otp.invalid'));
        setError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword.trim()) {
      setError(t('error.currentPassword.empty'));
      return;
    }

    if (!formData.newPassword.trim()) {
      setError(t('error.newPassword.empty'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('error.password.mismatch'));
      return;
    }

    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('\n'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          otpCode: otpCode,
          language
        }),
      });

      if (response.ok) {
        blobticMessage.success(t('success.passwordChanged'), t('dialog.changePassword.success'));
        setIsSuccess(true);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.code ? t(errorData.code) : (errorData.message || t('error.password.invalid'));
        setError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setOtpCode('');
    setError(null);
    setStep('email');
    setIsSuccess(false);
    onClose();
  };

  const renderEmailStep = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('dialog.changePassword.step.email')}
      </Typography>

      <TextField
        fullWidth
        label={t('login.email')}
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        margin="normal"
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <DialogActions>
        <Button onClick={handleClose}>{t('btn.cancel')}</Button>
        <Button
          onClick={handleRequestOtp}
          disabled={isSubmitting || !formData.email.trim()}
          variant="contained"
        >
          {isSubmitting ? <CircularProgress size={20} /> : t('btn.sendCode')}
        </Button>
      </DialogActions>
    </>
  );

  const renderVerificationStep = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('dialog.changePassword.step.verify', formData.email)}
      </Typography>

      <TextField
        fullWidth
        label={t('label.verificationCode')}
        value={otpCode}
        onChange={handleOtpChange}
        margin="normal"
        required
        inputProps={{ maxLength: 6 }}
        placeholder="000000"
      />

      <DialogActions>
        <Button onClick={() => setStep('email')}>{t('btn.back')}</Button>
        <Button
          onClick={handleVerifyOtp}
          disabled={isSubmitting || !otpCode.trim()}
          variant="contained"
        >
          {isSubmitting ? <CircularProgress size={20} /> : t('btn.verifyCode')}
        </Button>
      </DialogActions>
    </>
  );

  const renderNewPasswordStep = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('dialog.changePassword.step.new')}
      </Typography>

      <TextField
        fullWidth
        label={t('label.currentPassword')}
        type={showPasswords.current ? 'text' : 'password'}
        value={formData.currentPassword}
        onChange={handleInputChange('currentPassword')}
        margin="normal"
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility('current')}
                edge="end"
              >
                {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label={t('label.newPassword')}
        type={showPasswords.new ? 'text' : 'password'}
        value={formData.newPassword}
        onChange={handleInputChange('newPassword')}
        margin="normal"
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility('new')}
                edge="end"
              >
                {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label={t('label.confirmPassword')}
        type={showPasswords.confirm ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={handleInputChange('confirmPassword')}
        margin="normal"
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility('confirm')}
                edge="end"
              >
                {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {isSuccess ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('dialog.changePassword.success')}
          </Alert>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" color="primary">
              {t('btn.close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogActions>
          <Button onClick={() => setStep('verification')}>{t('btn.back')}</Button>
          <Button
            onClick={handleChangePassword}
            disabled={isSubmitting || !formData.currentPassword.trim() || !formData.newPassword.trim() || !formData.confirmPassword.trim()}
            variant="contained"
          >
            {isSubmitting ? <CircularProgress size={20} /> : t('btn.changePassword')}
          </Button>
        </DialogActions>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={() => { }} // Empêche la fermeture en cliquant en dehors
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <KeyIcon color="primary" />
          <Typography variant="h6" component="div">
            {t('dialog.changePassword.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'email' && renderEmailStep()}
        {step === 'verification' && renderVerificationStep()}
        {step === 'newPassword' && renderNewPasswordStep()}
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
